import React, { FC } from "react";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import { BrandModal } from "../_shared/BrandModal";

export type TutorialProps = {
  open: boolean;
  onClose: () => void;
};

type Step = {
  title: string;
  body: React.ReactNode;
};

const STEPS: Step[] = [
  {
    title: "Read the clues",
    body: (
      <>
        Across and Down clues, just like a crossword. They spell out the words
        the finished grid should contain.
      </>
    ),
  },
  {
    title: "Play your pieces in order",
    body: (
      <>
        The row beneath the board is your move sequence. The pill above the
        board shows who&rsquo;s up next &mdash; e.g.{" "}
        <Typography component="span" fontWeight="bold">
          Moving as Knight
        </Typography>
        .
      </>
    ),
  },
  {
    title: "Drag to capture",
    body: (
      <>
        Drop the active piece on a legal square (they light up as you drag).
        Landing on a tile captures it and clears that letter. Each piece moves{" "}
        <Typography component="span" fontWeight="bold" fontStyle="italic">
          once
        </Typography>
        , then the next piece steps up.
      </>
    ),
  },
  {
    title: "Chisel it down",
    body: (
      <>
        Keep capturing until the leftover letters spell the clued words &mdash;
        and <Typography component="span" fontWeight="bold" fontStyle="italic">only</Typography>{" "}
        those words. Solve it and the board is yours.
      </>
    ),
  },
];

export const Tutorial: FC<TutorialProps> = ({ open, onClose }) => {
  const handleTutorialClose = () => {
    localStorage.setItem("tutorial", "true");
    onClose();
  };

  return (
    <BrandModal
      open={open}
      onClose={handleTutorialClose}
      title="HOW-TO-PLAY"
      maxWidth="md"
    >
      <DialogContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={3}
          alignItems="flex-start"
        >
          {/* Looping, muted screen recording of a full solve */}
          <Box
            component="video"
            src={`${process.env.PUBLIC_URL}/chisel-tutorial.mp4`}
            autoPlay
            loop
            muted
            playsInline
            controls
            sx={{
              width: "100%",
              flex: { md: "1 1 58%" },
              borderRadius: 2,
              display: "block",
              backgroundColor: "rgba(0,0,0,0.04)",
            }}
          />

          <Stack spacing={2} flex={{ md: "1 1 42%" }}>
            <Box>
              <Typography variant="h6" gutterBottom>
                Chess meets crossword.
              </Typography>
              <Typography>
                Every board starts with more letters than you need. Capture the
                extras with a set sequence of chess pieces until only the answer
                remains.
              </Typography>
            </Box>

            <Stack component="ol" spacing={1.5} sx={{ m: 0, pl: 2.5 }}>
              {STEPS.map((step) => (
                <li key={step.title}>
                  <Typography component="span" fontWeight="bold">
                    {step.title}.
                  </Typography>{" "}
                  <Typography component="span">{step.body}</Typography>
                </li>
              ))}
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Pieces move exactly as they do in chess &mdash; knights in an L,
              bishops on the diagonal, and so on. A fresh puzzle drops every day.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          disableElevation
          onClick={handleTutorialClose}
        >
          Play Now
        </Button>
      </DialogActions>
    </BrandModal>
  );
};
